import React, { useEffect, useRef, useState, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useToast } from "../../../hooks/toast";
import * as Yup from "yup";
import api from "../../../service";
import { Form } from "@unform/web";
import getValidationErrors from "../../../utils/getValidationErrors";
import { validateSingleFieldOnBlur } from "../../../utils/formValidations";

import {
  FormContainer,
  TitleContainer,
  Title,
  FieldsContainer,
  Horizontal,
  FilterActionsContainer,
  SubmitContainer,
  FileDropzone,
} from "./styles";
import SelectWithLabel from "../../../components/SelectWithLabel";
import Modal from "../../../components/Modal";
import FormButton from "../../../components/FormButton";
import Dropzone from "../../../components/Dropzone";
import { useLoading } from "../../../hooks/loading";

const ExportGuest = ({ setIsOpen, modalIsOpen, updatePageCallback }) => {
  const { token } = useSelector((state) => state.auth);
  const formRef = useRef(null);
  const intl = useIntl();
  const { addToast } = useToast();
  const { handleLoading } = useLoading();

  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState();
  const [exhibitors, setExhibitors] = useState();

  const schema = Yup.object().shape({
    event: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    exhibitor: Yup.string()
      .nullable()
      .required(intl.formatMessage({ id: "error.field_required" })),
    file: Yup.array()
      .nullable()
      .min(1, intl.formatMessage({ id: "error.field_required" })),
  });

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current?.setErrors({});

        handleLoading(true);

        await schema.validate(data, { abortEarly: false });

        var formData = new FormData();

        data.file[0]
          .slice(0, 1)
          .arrayBuffer()
          .then(() => {
            formData.set("exhibitor", data.exhibitor);
            formData.set("file", data.file[0]);

            api
              .post(`/admin/manage-guests/${data.event}`, formData, {
                headers: {
                  Accept: "*/*",
                  Authorization: `Bearer ${token}`,
                },
              })
              .then((result) => {
                addToast({
                  type: "success",
                  title: intl.formatMessage({
                    id: "success",
                  }),
                  description: intl.formatMessage({
                    id: "success.guest_import",
                  }),
                });
                setIsOpen(false);
                updatePageCallback();
              })
              .catch((err) => {
                addToast({
                  type: "error",
                  title: intl.formatMessage({
                    id: "error",
                  }),
                  description: intl.formatMessage({
                    id: "api.error." + err.response.data.code,
                  }),
                });
              })
              .finally(() => {
                handleLoading(false);
              });
          })
          .catch(() => {
            handleLoading(false);
            addToast({
              type: "error",
              title: intl.formatMessage({
                id: "error",
              }),
              description: intl.formatMessage({
                id: "error.changed_file",
              }),
            });
            formRef.current.clearField("file");
          });
      } catch (err) {
        handleLoading(false);
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }
      }
    },
    [
      addToast,
      handleLoading,
      intl,
      schema,
      setIsOpen,
      token,
      updatePageCallback,
    ]
  );

  useEffect(() => {
    api
      .get(`admin/events`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
        },
      })
      .then((result) => {
        const events = result.data.output.map((event) => ({
          label: event.eventEditionName,
          value: event.eventEditionCode,
        }));
        let eventsSort = [...events].sort((a, b) =>
          a.label < b.label ? -1 : a.label > b.label ? 1 : 0
        );
        setEvents(eventsSort);
      })
      .catch((err) => {
        addToast({
          type: "error",
          title: intl.formatMessage({
            id: "error",
          }),
          description: intl.formatMessage({
            id: "api.error." + err.response.data.code,
          }),
        });
      });
  }, [addToast, intl, token]);

  useEffect(() => {
    if (selectedEvent) {
      api
        .get(`/users/exhibitor/event/${selectedEvent}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
          },
        })
        .then((result) => {
          const exhibitors = result.data.users.map((user) => ({
            label: user.commercialName,
            value: user.id,
          }));
          setExhibitors(exhibitors);
        })
        .catch((err) => {
          addToast({
            type: "error",
            title: intl.formatMessage({
              id: "error",
            }),
            description: intl.formatMessage({
              id: "api.error." + err.response.data.code,
            }),
          });
        });
    }
  }, [selectedEvent, addToast, intl, token]);

  const validationBlur = (name) => {
    let errors = formRef.current.getErrors();
    validateSingleFieldOnBlur(errors, schema, name, formRef);
  };

  return (
    <Modal setIsOpen={setIsOpen} modalIsOpen={modalIsOpen} modalWidth="50%">
      <FormContainer>
        <Form ref={formRef} onSubmit={handleSubmit}>
          <TitleContainer>
            <Title>
              <FormattedMessage id="upload_via_xls" />
            </Title>
          </TitleContainer>
          <FieldsContainer>
            <SelectWithLabel
              name="event"
              validationBlur={validationBlur}
              label={intl.formatMessage({ id: "event_edition" })}
              placeholder={intl.formatMessage({ id: "event_edition" })}
              onChange={(option) => setSelectedEvent(option.value)}
              labelOrientation="vertical"
              options={events}
              required
            />

            <SelectWithLabel
              name="exhibitor"
              validationBlur={validationBlur}
              label={intl.formatMessage({ id: "exhibitor" })}
              placeholder={intl.formatMessage({ id: "exhibitor" })}
              labelOrientation="vertical"
              options={exhibitors}
              required
            />
            <FileDropzone>
              <Dropzone
                name="file"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                type="XLSX"
                multiple={false}
              />
            </FileDropzone>
            <Horizontal>
              <FilterActionsContainer>
                <SubmitContainer>
                  <FormButton
                    color="#C9C9C9"
                    type="button"
                    onClick={() => setIsOpen(false)}
                  >
                    <FormattedMessage id="close" />
                  </FormButton>
                </SubmitContainer>
                <SubmitContainer>
                  <FormButton type="submit">
                    <FormattedMessage id="save" />
                  </FormButton>
                </SubmitContainer>
              </FilterActionsContainer>
            </Horizontal>
          </FieldsContainer>
        </Form>
      </FormContainer>
    </Modal>
  );
};

export default ExportGuest;
